import React, { useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";

import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { Button } from "@components/buttons";
import { ExpandedMessageObject } from "@data-types/chat";
import ChatNameForm from "@features/chat/desktop/ChatNameForm";
import { UploadedFilesPreview } from "@features/chat/shared";
import useFileHandler from "@features/chat/shared/useFileHandler";
import { DiagnosticsProvider } from "@state/providers/diagnosticsProvider";
import { ChatViewStore } from "@state/stores/chat/ChatViewStore";
import { observer } from "mobx-react";

import MessageContainer from "../MessageContainer";

export default observer(function ChatArea({
	store,
	diagnostics,
}: {
	store: ChatViewStore;
	diagnostics: DiagnosticsProvider;
}) {
	const { chatChannelHandler } = store;

	const chatContainerRef = useRef<HTMLDivElement>(null);
	const scrollingRef = useRef(null);
	const messageRef = useRef<HTMLDivElement>(null);

	const { t } = useTranslation();

	const pagination = chatChannelHandler.supportMessagePaginationObject;
	const messages = chatChannelHandler.supportChannelMessageList;

	const [scrollPosition, setScrollPosition] = useState(0);

	if (!store.chatDisplayName) {
		return <ChatNameForm store={store} />;
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/SupportChatArea.tsx"}
		>
			<>
				<div className="chat--support">
					<div
						ref={chatContainerRef}
						id="support-scrollableDiv"
						className="chat--support__content scrollbar--thin"
					>
						{messages && messages.totalMessages > 0 ? (
							<InfiniteScroll
								ref={scrollingRef}
								className="chat__scroll"
								onScroll={(e: any) => {
									setScrollPosition(e.target.scrollTop);
								}}
								dataLength={messages.totalMessages}
								next={() =>
									pagination?.onNextPage &&
									pagination.onNextPage()
								}
								hasMore={pagination?.hasNextPage || false}
								inverse={true}
								loader={
									<div className="chat__box__loader">
										<div className="loader__spinner">
											<div className="loader__spinner__wrapper"></div>
										</div>
									</div>
								}
								scrollableTarget="support-scrollableDiv"
								endMessage={
									messages.totalMessages > 30 && (
										<span className="chat__no-more">
											{t(
												"CHAT.NO_MORE_MESSAGES_AVAILABLE"
											)}
										</span>
									)
								}
							>
								<div id="support-bottom-scroll-div"></div>
								{Object.keys(messages.groupedMessages).map(
									(date) => (
										<React.Fragment key={date}>
											{messages.groupedMessages[date].map(
												(message) => (
													<div
														key={message.messageId}
														ref={messageRef}
													>
														<MessageContainer
															noReport
															noReply
															message={
																message as ExpandedMessageObject
															}
															support
														/>
													</div>
												)
											)}
											<div className="chat--support__date">
												<div className="chat--support__date__label">
													{date}
												</div>
											</div>
										</React.Fragment>
									)
								)}
								<div id="reply-scroll-div"></div>
							</InfiniteScroll>
						) : (
							<span className="chat__no-more">
								{t("CHAT.NO_MESSAGES")}
							</span>
						)}
					</div>
					{scrollPosition < 0 && (
						<ScrollToBottomButton
							chatContainerRef={chatContainerRef}
						/>
					)}
					<ChatMessageForm
						showInfo={
							(messages?.totalMessages || 0) < 1 ||
							messages?.showAdditionalInfo
						}
						chatContainerRef={chatContainerRef}
						diagnostics={diagnostics}
						store={store}
					/>
				</div>
			</>
		</ErrorBoundaryWithPropagation>
	);
});

function ScrollToBottomButton({
	chatContainerRef,
}: {
	chatContainerRef: React.RefObject<HTMLDivElement>;
}) {
	function scrollToBottom() {
		if (chatContainerRef.current) {
			chatContainerRef.current.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		}
	}

	return (
		<button
			type="button"
			className="chat__btn--scroll btn btn--round btn--round--sml btn--icon btn--tertiary"
			onClick={scrollToBottom}
		>
			<i className="is-dark u-icon u-icon--arrow--down u-icon--tny" />
		</button>
	);
}

const ChatMessageForm = observer(function ChatMessageForm({
	chatContainerRef,
	store,
	diagnostics,
	showInfo,
}: {
	store: ChatViewStore;
	chatContainerRef: React.RefObject<HTMLDivElement>;
	diagnostics: DiagnosticsProvider;
	showInfo?: boolean;
}) {
	const { t } = useTranslation();

	const textInputRef = useRef<HTMLTextAreaElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const [inputValue, setInputValue] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { chooseFile, files, removeFile, setFiles } = useFileHandler(
		inputRef,
		textInputRef
	);
	const sendMsg = async (
		e:
			| React.FormEvent<HTMLFormElement>
			| React.KeyboardEvent<HTMLTextAreaElement>
	) => {
		setIsSubmitting(true);
		e.preventDefault();

		await store.chatChannelHandler.sendSupportMsg(
			inputValue,
			files[0],
			diagnostics.errorMessages
		);

		diagnostics.onDispose();
		setFiles([]);
		setInputValue("");
		setIsSubmitting(false);

		chatContainerRef.current?.scrollTo({
			top: 0,
		});
	};

	return (
		<form onSubmit={sendMsg} className="chat__footer chat--support__footer">
			{showInfo && (
				<InfoMessageComponent
					type="default"
					size="sml"
					addClassName="chat--support__notice"
					customIcon="u-icon--med u-icon--support--alt"
					message={t("CHAT.SUPPORT.ADDITIONAL_INFO")}
				/>
			)}

			{files && files.length > 0 && (
				<UploadedFilesPreview removeFile={removeFile} files={files} />
			)}

			<div className="chat__textbox chat--support__textbox">
				<textarea
					maxLength={10000}
					ref={textInputRef}
					onKeyDown={(e) => {
						if (e.key === "Enter" && e.shiftKey == false) {
							e.preventDefault();
							if (inputValue.length > 0 || files.length > 0) {
								sendMsg(e);
							}
						}
					}}
					id="chat-textarea"
					value={inputValue}
					rows={3}
					className="chat__textarea chat--support__textarea input input--med input--textarea"
					placeholder={t("CHAT.ENTER_MESSAGE")}
					onChange={(e) => setInputValue(e.target.value)}
					autoFocus
				></textarea>

				<div className="chat--support__actions">
					<label
						htmlFor="files"
						className="chat__file btn btn--square btn--square--base btn--ghost u-mar--right--xsml"
					>
						<i className="u-icon u-icon--base u-icon--attachment u-color--text--secondary" />
					</label>
					<input
						ref={inputRef}
						name="files"
						id="files"
						accept="image/*, video/*"
						type="file"
						onChange={chooseFile}
						className="u-display--none"
						disabled={files.length > 0}
					/>

					<Button
						className="chat__send btn--square btn--square--base btn--accent"
						iconOnly
						iconClassName="u-icon--base u-icon--arrow--send u-color--accent--text"
						disabled={
							(inputValue.length < 1 && files.length < 1) ||
							isSubmitting
						}
					/>
				</div>
			</div>
		</form>
	);
});
